import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import cx from 'classnames';
import { injectIntl } from 'react-intl';

import withStyles from 'isomorphic-style-loader/withStyles';

import Grid from '../Grid';
import Label from '../Label/Label';
import Headroom from '../Headroom';
import RadioGroup from '../RadioGroup';
import Field from '../FormField/FormField';
import ProductImageGallery from './components/ProductImageGallery';
import Usps from './components/Usps';

import { createMarkup, priceFormat } from '../../helpers';
import {
  formMessages,
  productMessages,
  shopMessages,
} from '../../defineMessages';
import withResponsiveWrapper from '../../decorators/withResponsiveWrapper';
import { ICONS, PRODUCT_TYPE } from '../../constants';
import Price from '../Price/Price';
import AutoSuggest from '../AutoSuggest/AutoSuggest';

// eslint-disable-next-line css-modules/no-unused-class
import s from './ProductInfo.scss';

const ProductInfo = ({
  product,
  intl: { formatMessage, formatNumber },
  isMobile,
  className,
}) => {
  const {
    data: {
      title,
      excerpt,
      logoSquare,
      description,
      usps: productUsps = [],
      gallery,
      deliveryMethods = [],
      productType,
    },
  } = product;
  const [selectedDeliveryMethod, setDeliveryMethod] = useState();

  const showDeliveryOptions =
    deliveryMethods?.length !== 0 &&
    productType !== PRODUCT_TYPE.physicalProduct;

  useEffect(() => {
    if (showDeliveryOptions) setDeliveryMethod(Object.keys(deliveryMethods)[0]);
  }, [deliveryMethods, showDeliveryOptions]);

  const usps = productUsps.filter(({ content }) => !!content);

  const options = {
    offset: 160,
    classes: {
      pinned: s.headroomPinned,
      unpinned: s.headroomUnpinned,
      top: s.headroomTop,
      notTop: s.headroomNotTop,
      initial: s.root,
    },
  };

  const deliveryOptions = deliveryMethods
    ? Object.keys(deliveryMethods).map(key => ({
        value: key,
        label: formatMessage({ ...formMessages[key] }),
        isSelected: selectedDeliveryMethod === key,
        icon: ICONS[`DELIVERY_${key.toUpperCase()}`],
        onChange: (_name, value) => setDeliveryMethod(value),
      }))
    : [];

  const amounts = deliveryMethods[selectedDeliveryMethod];

  const labelAttributes = attributes =>
    attributes.length > 0
      ? attributes.reduce(
          (sum, { name }) => (sum === '' ? name : `${sum}, ${name}`),
          '',
        )
      : '';

  const amountOptions = amounts?.map(
    ({ defaultPriceCurrency, defaultPrice, attributes }) => ({
      value: defaultPrice,
      label: priceFormat({
        value: defaultPrice,
        currency: defaultPriceCurrency,
        defaultCurrency: defaultPriceCurrency,
        formatNumber,
        prefix: labelAttributes(attributes),
        parenthesis: attributes.length !== 0,
        showCurrency: true,
      }),
      inStock: true,
    }),
  );

  return (
    <Grid container spacing={16} className={cx(s.main, className || '')}>
      <Grid item xs={24} sm={24} md={5}>
        <Headroom options={options}>
          <div className={cx(s.panel, s.gallery)}>
            {gallery && <ProductImageGallery title={title} gallery={gallery} />}
          </div>
        </Headroom>
      </Grid>
      <Grid item xs={24} sm={24} md={11}>
        <div className={cx(s.panel, s.padding, s.noPaddingMobile)}>
          <Grid
            container
            spacing={16}
            className={cx(s.summary, s.mobilePanel, isMobile && s.space)}
          >
            <Grid item md={4} className="hidden-xs">
              <img
                className={cx(s['logo-sm'], 'hidden-sm')}
                src={logoSquare}
                alt={formatMessage(productMessages.altProductLogo, {
                  title,
                })}
              />
            </Grid>
            <Grid item sm={24} md={20} className={cx('sm-align-center')}>
              <Label bold className={s.title} label={title} tag="h1" />
              <p className={s.excerpt}>{excerpt}</p>
            </Grid>
            {usps.length !== 0 && isMobile && (
              <div className={cx(s.carousel)}>
                <Usps usps={usps} />
              </div>
            )}
          </Grid>
          {!isMobile && (
            <div className={cx(s.padding, s.usps)}>
              <Usps usps={usps} />
            </div>
          )}
          {description && (
            <div className={cx(s.padding, s.mobilePanel, s.productDescription)}>
              <div dangerouslySetInnerHTML={createMarkup(description)} />
            </div>
          )}
        </div>
      </Grid>

      <Grid item xs={24} sm={24} md={8}>
        <Headroom options={options}>
          <div className={cx(s.panel, !isMobile && s.padding, s.widthFix)}>
            {showDeliveryOptions ? (
              <div className={cx(s.section, s.hideIcons)}>
                <Label tag="h3" bold className={cx(s.title, s['form-label'])}>
                  {formatMessage({ ...formMessages.delivery })}
                </Label>
                <Field
                  input={RadioGroup}
                  name="deliveryMethod"
                  radioProps={deliveryOptions}
                  fluid
                />
              </div>
            ) : null}
            <div className={s.section}>
              <Grid
                container
                direction={
                  productType === PRODUCT_TYPE.physicalProduct
                    ? 'column'
                    : 'row'
                }
                spacing={16}
                wrap="wrap"
              >
                {amountOptions?.length > 0 && (
                  <>
                    {productType === PRODUCT_TYPE.physicalProduct ? (
                      <>
                        <hr className={s['divider-line']} />
                        <Grid item xs={24}>
                          <Label tag="h3" bold>
                            {formatMessage({ ...shopMessages.yourPrice })}{' '}
                            <Price
                              value={amounts.price}
                              currency={amounts.defaultPriceCurrency}
                            />
                          </Label>
                        </Grid>
                        <hr className={s['divider-line']} />
                      </>
                    ) : (
                      <>
                        <Grid item xs={24}>
                          <Label
                            tag="h3"
                            bold
                            className={cx(s.title, s['form-label'])}
                          >
                            {formatMessage({ ...formMessages.amount })}
                          </Label>
                          <Field
                            basic
                            fluid
                            input={AutoSuggest}
                            className={s['form-label']}
                            id="giftcardValue"
                            name="giftcardValue"
                            options={amountOptions}
                            onChange={value => value}
                            placeHolder={formatMessage({
                              ...formMessages.amount,
                            })}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </div>
          </div>
        </Headroom>
      </Grid>
    </Grid>
  );
};

const mapState = state => ({
  product: state.product,
});

export default compose(
  connect(mapState),
  injectIntl,
  withStyles(s),
  withResponsiveWrapper(),
)(ProductInfo);

ProductInfo.propTypes = {
  product: PropTypes.shape().isRequired,
  intl: PropTypes.shape().isRequired,
  isMobile: PropTypes.bool.isRequired,
  className: PropTypes.string.isRequired,
};
